import getFeedMetaData from '@dehaat/kisan-app-bl/api/getFeedMetaData';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import { CustomFeed, FeedType, ViewType } from '@dehaat/kisan-app-bl/models/Feed';
import dynamic from 'next/dynamic';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CART_TYPE } from '@/constants/cart';
import { CACHE_EXPIRY_TIME, CLIENT_NAME_COOKIE } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import { useAppSelector } from '@/hooks/reduxHooks';
import { clearFeedMetaResponse, selectFeedMetaData, setFeedMetaResponse } from '@/store/features/cacheSlice';
import { selectCurrentVendorId, selectLatLng, selectLoadingHyperlocalData, selectLoadingOndcVendors, selectOndcVendorId, selectVendorsList } from '@/store/features/locationSlice';
import { getCookieValue, isArray, isFromAgcApp } from '@/utils/helper';
import ComboGrid from '../homeV2/ComboGrid';
import HorizontalList from '../homeV2/HorizontalList';
import ProductShorts from '../homeV2/ProductShorts';
import VendorList from '../homeV2/VendorList';
import OneXListLoading from './filterOneX/OneXListLoading';
import ProductOneXHorizontalListLoading from './productOneX/ProductOneXHorizontalListLoading';
const BannerCarousel = dynamic(() => import('./BannerCarousel'));
const BannerCarouselV2 = dynamic(() => import('../homeV2/BannerCarouselV2'));
const CouponBanner = dynamic(() => import('./CouponBanner'));
const ProductScroll2x = dynamic(() => import('./ProductScrollV2'));
const ProductBannerGrid = dynamic(() => import('./ProductBannerGrid'));
const ProductBannerGridV2 = dynamic(() => import('../homeV2/ProductBannerGridV2'));
const Scrollable1XDesktopGrid = dynamic(() => import('../homeV2/Scrollable1XDesktopGrid'));
const OneXList = dynamic(() => import('./filterOneX/OneXList'));
const Grid = dynamic(() => import('./Grid'));
const GridV2 = dynamic(() => import('../homeV2/GridV2'));
const ProductOneXHorizontalList = dynamic(() => import('./productOneX/ProductOneXHorizontalList'));
const ScrollableList2X = dynamic(() => import('./ScrollableList2X'));
const SimpleBannerWithCTAList = dynamic(() => import('./SimpleBannerWithCTAList'));
interface Props {
  feedList: CustomFeed[];
  locale?: string;
  activeTab?: string;
}
const FeedManager = ({
  feedList,
  locale = DEFAULT_LANG,
  activeTab
}: Props) => {
  const {
    cartType
  } = useContext(CartContext);
  const [feedMetaData, setFeedMetaData] = useState<FeedType[]>([]);
  const isONDC = cartType === CART_TYPE.ONDC;
  const isHyperlocalDataLoading = useAppSelector(isONDC ? selectLoadingOndcVendors : selectLoadingHyperlocalData);
  const [loading, setLoading] = useState(true);
  const [remainingLoading, setRemainingLoading] = useState(false);
  const dispatch = useDispatch();
  const latLng = useAppSelector(selectLatLng);
  const vendorId = useAppSelector(isONDC ? selectOndcVendorId : selectCurrentVendorId);
  const sectionIds = useMemo(() => feedList.map(feedItem => feedItem.section_id), [feedList]);
  const cache = useSelector(selectFeedMetaData);
  const vendorList = useAppSelector(selectVendorsList);
  useEffect(() => {
    window.scrollTo(0, 0);
    setFeedMetaData([]);
  }, [activeTab]);
  const getSectionMetaData = useCallback(async (ids: string, isLoding = true) => {
    const vendor = vendorId ? Number(vendorId) : undefined;
    const key = JSON.stringify([ids, vendor, latLng, locale, isONDC]);
    if (cache && cache[key]) {
      setLoading(false);
      setFeedMetaData(prev => [...prev, ...cache[key]]);
    } else {
      isLoding ? setLoading(true) : setRemainingLoading(true);
      try {
        const response = await getFeedMetaData(ids, vendor, undefined, latLng, locale, undefined, isONDC);
        setFeedMetaData(prev => [...prev, ...response]);
        dispatch(setFeedMetaResponse({
          [key]: response
        }));
        setTimeout(() => {
          dispatch(clearFeedMetaResponse(key));
        }, CACHE_EXPIRY_TIME);
      } catch (error) {
        console.error('Error fetching metadata:', error);
      }
      isLoding ? setLoading(false) : setRemainingLoading(false);
    }
  }, [latLng, vendorId, isHyperlocalDataLoading, isONDC, locale, feedList.length]);
  useEffect(() => {
    if (!sectionIds.length || isHyperlocalDataLoading) return;
    const BATCH_SIZE = 2;
    const loadInBatches = async () => {
      for (let i = 0; i < sectionIds.length; i += BATCH_SIZE) {
        const batchIds = sectionIds.slice(i, i + BATCH_SIZE).join(',');
        const isInitial = i === 0;
        await getSectionMetaData(batchIds, isInitial);
      }
    };
    loadInBatches();
  }, [sectionIds.join(','), isHyperlocalDataLoading, getSectionMetaData]);
  return <section className="space-y-4 mt-2" data-sentry-component="FeedManager" data-sentry-source-file="FeedManager.tsx">
      {feedList.map(({
      section_id,
      view_type
    }) => {
      if (loading) {
        switch (view_type as ViewType | 'filter_vertical_1x') {
          case 'filter_scroll_1x':
            return <OneXListLoading key={section_id} count={5} sectionId={section_id} isVertical={false} />;
          case 'filter_vertical_1x':
            return <OneXListLoading key={section_id} count={5} sectionId={section_id} isVertical />;
          case 'product_scroll_1x':
            return <ProductOneXHorizontalListLoading count={5} key={section_id} sectionId={section_id} />;
          default:
            return <div key={section_id} className="bg-neutral-20 mx-4 rounded-lg h-32 animate-pulse" />;
        }
      }
      const metaData = feedMetaData.find(metaData => metaData.id === section_id);
      if (!metaData || !isArray(metaData.data)) {
        return null;
      }
      const props = {
        loading,
        metaData,
        sectionId: section_id
      };
      const fromAgc = isFromAgcApp(getCookieValue(CLIENT_NAME_COOKIE));
      switch (view_type as ViewType | 'filter_vertical_1x') {
        case 'filter_scroll_1x':
          return <OneXList {...props} key={section_id} />;
        case 'filter_vertical_1x':
          return <OneXList {...props} key={section_id} isVertical />;
        case 'product_scroll_1x':
          return <ProductOneXHorizontalList {...props} key={section_id} />;
        case 'scroll_2x':
          return <ScrollableList2X {...props} key={section_id} />;
        case 'simple_banner_with_cta':
          return <SimpleBannerWithCTAList {...props} key={section_id} />;
        case 'simple_banner':
          return <BannerCarousel {...props} key={section_id} />;
        case 'simple_banner_v2':
          return <BannerCarouselV2 {...props} key={section_id} />;
        case 'product_grid':
          return <ProductBannerGrid {...props} key={section_id} />;
        case 'product_grid_v2':
          return <ProductBannerGridV2 {...props} key={section_id} />;
        case 'grid':
          return <Grid {...props} key={section_id} />;
        case 'grid_v2':
          return <GridV2 {...props} key={section_id} />;
        case 'scroll_1x_desktop_grid':
          return <Scrollable1XDesktopGrid {...props} key={section_id} />;
        case 'coupon_banner':
          return <CouponBanner {...props} key={section_id} />;
        case 'product_scroll_1x_v2':
          return <ProductScroll2x {...props} key={section_id} />;
        case 'shorts_products_scroll_1x':
          return <ProductShorts {...props} key={section_id} />;
        case 'vendor_list':
          return !fromAgc ? <VendorList vendors={vendorList} {...props} key={section_id} /> : null;
        case 'horizontal_list':
          return <HorizontalList {...props} key={section_id} />;
        case 'combo_grid':
          return <ComboGrid metaData={metaData} key={section_id} />;
        default:
          return null;
      }
    })}
      {remainingLoading && <div className="bg-neutral-20 mx-4 rounded-lg h-32 animate-pulse" />}
    </section>;
};
export default FeedManager;